import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Menu, X, HelpCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { CurrencySelector } from '../Business';
import { LanguageSelector, SkinSwitcherDropdown } from '../Navigation';
import { ColorPicker } from '../Theme';
import { KombyLogo } from '../Branding/KombyLogo';
import { useTranslation } from 'react-i18next';
import { AuthButtons } from '../UI';
import { SkinSelectorTooltip } from '../SkinSelectorTooltip';

const StaggeredMobileMenu = lazy(() =>
  import('./StaggeredMobileMenu').then((m) => ({ default: m.StaggeredMobileMenu }))
);

interface HeaderProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  currentPage?: string;
  setCurrentPage?: (page: string) => void;
  adminMode?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ 
  isMenuOpen, 
  setIsMenuOpen, 
  currentPage = 'home',
  setCurrentPage,
  adminMode = false
}) => {
  const { colorScheme } = useTheme();
  const { t, ready } = useTranslation('common');
  const [showSkinTooltip, setShowSkinTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);

  // Détecter si on est sur mobile pour SUPPRIMER complètement les éléments desktop (pas juste masquer)
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Afficher le tooltip sur TOUTES les pages SAUF ThemeSelector, Coming Soon ET mobile
  useEffect(() => {
    // Exclure ThemeSelector (modal visible), Coming Soon et mobile
    const isThemeSelectorVisible = document.querySelector('[data-testid="theme-selector-modal"]') !== null;
    const isMobile = window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    const hasSeenTooltip = sessionStorage.getItem('skin-tooltip-shown-this-session');
    
    if (!hasSeenTooltip && !adminMode && !isThemeSelectorVisible && !isMobile) {
      setShowSkinTooltip(true);
      sessionStorage.setItem('skin-tooltip-shown-this-session', 'true');
    } else if (isThemeSelectorVisible || isMobile) {
      setShowSkinTooltip(false);
    }
  }, [currentPage, adminMode]);

  const handleNavClick = (page: string) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    }
    setIsMenuOpen(false);
  };
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 dark:bg-gray-900/80 theme-black:bg-transparent border-b border-gray-200 dark:border-gray-800 transition-all duration-300">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button 
              onClick={() => handleNavClick('home')}
              className="flex items-center cursor-pointer"
            >
              <KombyLogo className="w-10 h-10" animate={false} size={40} />
            </button>
          </div>

          {/* Desktop Navigation (hidden in adminMode) - visible UNIQUEMENT à partir de lg (1024px) */}
          {!adminMode && (
            <nav className="hidden lg:flex items-center space-x-4">
              <a
                onClick={() => handleNavClick('home')}
                className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 ${
                  currentPage === 'home' 
                    ? 'text-accent1 bg-accent1/10 dark:bg-accent1/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {t('navbar.home')}
              </a>
              <a
                onClick={() => handleNavClick('pricing')}
                className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 ${
                  currentPage === 'pricing' 
                    ? 'text-accent1 bg-accent1/10 dark:bg-accent1/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {t('navbar.pricing')}
              </a>
              <a
                onClick={() => handleNavClick('music')}
                className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 ${
                  currentPage === 'music' 
                    ? 'text-accent1 bg-accent1/10 dark:bg-accent1/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  🎵 {t('navbar.music')}
                </span>
              </a>
              <a
                onClick={() => handleNavClick('compare')}
                className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 ${
                  currentPage === 'compare' 
                    ? 'text-accent1 bg-accent1/10 dark:bg-accent1/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {t('navbar.compare')}
              </a>
              <a
                onClick={() => handleNavClick('contact')}
                className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 ${
                  currentPage === 'contact' 
                    ? 'text-accent1 bg-accent1/10 dark:bg-accent1/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {t('navbar.contact')}
              </a>
              <a
                onClick={() => handleNavClick('help')}
                className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 ${
                  currentPage === 'help' 
                    ? 'text-accent1 bg-accent1/10 dark:bg-accent1/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <HelpCircle className="h-4 w-4" />
                <span>{t('navbar.support')}</span>
              </a>
            </nav>
          )}

          {/* Theme Toggle & Mobile Menu */}
          <div className="flex items-center space-x-3">
            {/* Desktop selectors and auth buttons - visible UNIQUEMENT à partir de lg (1024px) */}
            {/* Utiliser UNIQUEMENT Tailwind hidden lg:flex - pas de condition JS */}
            {/* Mount desktop selectors only on lg+ (hidden lg:flex still mounts children otherwise → listeners on mobile) */}
            {!isMobile && (
              <div className="flex items-center space-x-2">
                {!adminMode && (
                  <>
                    <SkinSwitcherDropdown />
                    <ColorPicker />
                  </>
                )}
                <LanguageSelector />
                <CurrencySelector />
                <AuthButtons onNavigate={handleNavClick} />
              </div>
            )}

            {/* Mobile menu button - only show hamburger menu (visible jusqu'à lg pour tablettes) */}
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              className="lg:hidden p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 transition-all duration-200"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              ) : (
                <Menu className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation — lazy chunk only when opened (INP) */}
      {isMenuOpen && (
        <Suspense fallback={null}>
          <StaggeredMobileMenu
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            currentPage={currentPage}
            onNavigate={handleNavClick}
          />
        </Suspense>
      )}

      {/* Skin Selector Tooltip - Affiche "Choose your universe" sur toutes les pages */}
      <SkinSelectorTooltip
        isVisible={showSkinTooltip}
        onClose={() => setShowSkinTooltip(false)}
      />
    </header>
  );
};